import { FC, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AdminLayout } from "../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../components";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getManagerAccessLogsAction,
  IManagerAccessLogFilters,
} from "../../../redux/actions/managerAccessLogActions";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ACTION_TYPES = [
  { value: "", label: "All Actions" },
  { value: "CASH_REGISTER_ACCESS", label: "Cash Register Access" },
  { value: "ADD_CASH", label: "Add Cash" },
  { value: "REMOVE_CASH", label: "Remove Cash" },
];

const STATUS_COLORS: Record<string, string> = {
  SUCCESS: "#2e7d32",
  FAILED: "#c62828",
};

export const ManagerAccessLogsPage: FC = () => {
  const dispatch = useAppDispatch();
  const { logs, logsLoading, logsError } = useAppSelector(
    (state) => state.managerAccessLog
  );

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [actionType, setActionType] = useState<string>("");

  const fetchLogs = (filters: IManagerAccessLogFilters = {}) => {
    dispatch(getManagerAccessLogsAction(filters));
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleApplyFilters = () => {
    const filters: IManagerAccessLogFilters = {};
    if (dateFrom) filters.dateFrom = new Date(dateFrom).toISOString();
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filters.dateTo = to.toISOString();
    }
    if (actionType) filters.actionType = actionType;
    fetchLogs(filters);
  };

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setActionType("");
    fetchLogs();
  };

  const formatDateTime = (isoString: string) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString();
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{ fontSize: "1.714rem", fontWeight: "bold", color: Colors.MAIN_GRAY }}
          >
            Manager Access Logs
          </Typography>
        </Box>

        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            alignItems: "flex-end",
            flexWrap: "wrap",
            padding: "1.5rem",
            backgroundColor: "#f5f5f5",
            borderRadius: "0.5rem",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <Typography sx={{ fontSize: "0.857rem", color: Colors.MAIN_GRAY }}>
              From Date
            </Typography>
            <TextField
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              size="small"
              sx={{ width: "180px", backgroundColor: "#fff" }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <Typography sx={{ fontSize: "0.857rem", color: Colors.MAIN_GRAY }}>
              To Date
            </Typography>
            <TextField
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              size="small"
              sx={{ width: "180px", backgroundColor: "#fff" }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <Typography sx={{ fontSize: "0.857rem", color: Colors.MAIN_GRAY }}>
              Action Type
            </Typography>
            <Select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              size="small"
              displayEmpty
              sx={{ width: "220px", backgroundColor: "#fff" }}
            >
              {ACTION_TYPES.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ display: "flex", gap: "0.5rem", marginBottom: "1px" }}>
            <CustomButton
              onClick={handleApplyFilters}
              sx={{ height: "2.5rem", paddingInline: "1.5rem", fontSize: "0.857rem" }}
            >
              Apply Filters
            </CustomButton>
            <CustomButton
              onClick={handleClearFilters}
              sx={{
                height: "2.5rem",
                paddingInline: "1.5rem",
                fontSize: "0.857rem",
                backgroundColor: "#757575",
              }}
            >
              Clear
            </CustomButton>
          </Box>
        </Box>

        {/* Table */}
        {logsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
            <CircularProgress />
          </Box>
        ) : logsError ? (
          <Typography sx={{ color: "red", textAlign: "center" }}>
            Error: {logsError}
          </Typography>
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "calc(100vh - 22rem)",
              "&::-webkit-scrollbar": { width: "0.5rem", height: "0.5rem" },
              "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
              "&::-webkit-scrollbar-thumb": { background: "#888" },
              "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>#</StyledTableCell>
                  <StyledTableCell>Action Type</StyledTableCell>
                  <StyledTableCell>Requested By</StyledTableCell>
                  <StyledTableCell>Granted By</StyledTableCell>
                  <StyledTableCell>Station</StyledTableCell>
                  <StyledTableCell>Timestamp</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", padding: "2rem" }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => (
                    <StyledTableRow key={log.id}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>
                        {log.actionType.replace(/_/g, " ")}
                      </StyledTableCell>
                      <StyledTableCell>{log.requestedByName}</StyledTableCell>
                      <StyledTableCell>
                        {log.grantedByName ?? (
                          <Typography sx={{ color: "#999", fontStyle: "italic", fontSize: "0.857rem" }}>
                            Unknown
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        {log.cashierPointName ?? "-"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatDateTime(log.createdAt)}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          sx={{
                            color: STATUS_COLORS[log.status] ?? "#555",
                            fontWeight: "bold",
                            fontSize: "0.857rem",
                          }}
                        >
                          {log.status}
                        </Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
};
